import React, { type FC, forwardRef, useEffect, useRef } from 'react'
import { type CustomContentProps, useSnackbar } from 'notistack'

import { selectToasts, type ToastItem } from '../../../domain'

import { useAppState, useAppUsecase } from '../State'
import { notificationToastAutoHideDurationMs, Toast } from '../common/Notifications'

type ToastSnackbarProps = Readonly<{ item: ToastItem }>

declare module 'notistack' {
    interface VariantOverrides {
        toast: ToastSnackbarProps
    }
}

/**
 * Reads the live toast from app state by id rather than trusting the `item` prop notistack was
 * given at enqueue time - `ToastPortal` never re-enqueues an existing toast (see its own comment),
 * so that prop is a frozen snapshot from first render. Falls back to it only for the brief window
 * after `dismiss` has removed the toast from state but this snack hasn't finished unmounting yet.
 *
 * `Toast` itself owns the actual auto-dismiss timer (as well as the countdown bar): notistack's own
 * `autoHideDuration` fires once, counted from enqueue time, and can't be restarted without closing
 * and reopening the snack, so it's deliberately left unset below. `Toast` resets its own timer on
 * `count` changing, which is what makes an aggregated repeat (`ToastUsecase`, Step 4) reset the
 * dismiss countdown without any visual close/reopen. `autoHideDurationMs` is `undefined` for a
 * `persist` toast (Step 9) - it's the caller's job to call `dismiss()` when the state it reflects is
 * resolved.
 */
export const ToastSnackbarContent = forwardRef<HTMLDivElement, CustomContentProps & ToastSnackbarProps>(({ item }, ref) => {
    const toastUsecase = useAppUsecase('toastUsecase')
    const liveItem = useAppState((state) => selectToasts(state).find((toast) => toast.id === item.id), [item.id]) ?? item
    const autoHideDurationMs = liveItem.persist ? undefined : notificationToastAutoHideDurationMs[liveItem.severity]

    return (
        <div ref={ref}>
            <Toast
                severity={liveItem.severity}
                title={liveItem.title}
                description={liveItem.description}
                action={liveItem.action}
                count={liveItem.count}
                autoHideDurationMs={autoHideDurationMs}
                onClose={() => toastUsecase.dismiss(item.id)}
            />
        </div>
    )
})

ToastSnackbarContent.displayName = 'ToastSnackbarContent'

/**
 * Subscribes to the Toast queue (`ToastUsecase`, Step 4) and drives notistack's imperative
 * enqueue/close API to keep the visible snackbars in sync. Notistack still owns the portal,
 * stacking, and dismiss transitions/timing - this only decides what should currently be shown,
 * matching the "not a component's job" reasoning in the migration plan's target architecture.
 *
 * Keyed by plain `item.id` - stable across an aggregated repeat, unlike an earlier version of this
 * file that keyed by `id::count`. That composite key made every aggregation increment close and
 * reopen the snack (notistack has no in-place "update" API), which is a real transition - visually
 * indistinguishable from a brand new toast entering the stack. That collided with the "event toast
 * cascade" stacking animation once actually seen side by side, so a genuinely new toast and a
 * counter simply ticking up looked the same. A stable key means an aggregated repeat is enqueued
 * only once; `ToastSnackbarContent` above re-renders itself from live state instead.
 *
 * The `onClose` passed to `enqueueSnackbar` below is a safety net, not the primary dismiss path -
 * `dismiss` is normally called directly (the close button, or the auto-hide timer in
 * `ToastSnackbarContent`), which removes the toast from state and lets the effect below request the
 * actual `closeSnackbar`. This just keeps state in sync if the snack is ever closed some other way.
 */
export const ToastPortal: FC = () => {
    const toasts = useAppState(selectToasts)
    const toastUsecase = useAppUsecase('toastUsecase')
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()
    const rendered = useRef<ReadonlySet<string>>(new Set())

    useEffect(() => {
        const current = new Set(toasts.map((item) => item.id))

        for (const id of rendered.current) {
            if (!current.has(id)) {
                closeSnackbar(id)
            }
        }

        for (const item of toasts) {
            if (!rendered.current.has(item.id)) {
                enqueueSnackbar(item.title, {
                    key: item.id,
                    variant: 'toast',
                    item,
                    // null, not omitted: notistack defaults to a 5s auto-hide when this is left unset, which would
                    // race the real timer `ToastSnackbarContent` manages below with notistack's own, unrelated one
                    autoHideDuration: null,
                    onClose: () => toastUsecase.dismiss(item.id),
                })
            }
        }

        rendered.current = current
    }, [toasts, toastUsecase, enqueueSnackbar, closeSnackbar])

    return null
}
